import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { api, type SheetRow } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/Skeletons';

interface CreateSheetPayload {
  display_name: string;
  spreadsheet_id: string;
  worksheet_name: string;
}

interface CheckResult {
  ok: boolean;
  error?: string;
  share_with?: string | null;
}

function extractSpreadsheetId(input: string): string {
  const m = input.match(/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
  return m?.[1] ?? input.trim();
}

export function Sheets(): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<CreateSheetPayload>({
    display_name: '',
    spreadsheet_id: '',
    worksheet_name: 'Sheet1',
  });
  const [checkResults, setCheckResults] = useState<Record<string, CheckResult>>({});
  const queryClient = useQueryClient();

  const { data = [], isLoading } = useQuery({
    queryKey: ['sheets'],
    queryFn: () => api.get<SheetRow[]>('/admin/sheets'),
  });

  const { data: sa } = useQuery({
    queryKey: ['service-account'],
    queryFn: () => api.get<{ email: string | null; error?: string }>('/admin/service-account'),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateSheetPayload) => api.post<SheetRow>('/admin/sheets', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sheets'] });
      setIsOpen(false);
      setForm({ display_name: '', spreadsheet_id: '', worksheet_name: 'Sheet1' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/sheets/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sheets'] }),
  });

  const checkAccess = async (sheet: SheetRow): Promise<void> => {
    try {
      const result = await api.post<CheckResult>(`/admin/sheets/${sheet.id}/check`);
      setCheckResults((prev) => ({ ...prev, [sheet.id]: result }));
    } catch (err) {
      setCheckResults((prev) => ({
        ...prev,
        [sheet.id]: { ok: false, error: (err as Error).message, share_with: sa?.email ?? null },
      }));
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Google Sheets</h1>
          <p className="text-sm text-muted-foreground">
            Таблицы, в которые синхронизируются сделки Kommo
          </p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4" />
              Добавить таблицу
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate({ ...form, spreadsheet_id: extractSpreadsheetId(form.spreadsheet_id) });
              }}
              className="space-y-4"
            >
              <DialogHeader>
                <DialogTitle>Новая Google-таблица</DialogTitle>
                <DialogDescription>
                  {sa?.email ? (
                    <>
                      Расшарьте таблицу на{' '}
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{sa.email}</code> с
                      правом «Редактор».
                    </>
                  ) : (
                    'Service account ещё не настроен'
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <Label htmlFor="display_name">Название</Label>
                <Input
                  id="display_name"
                  placeholder="ClientX — сделки"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="spreadsheet_id">Spreadsheet ID или URL</Label>
                <Input
                  id="spreadsheet_id"
                  placeholder="https://docs.google.com/spreadsheets/d/…"
                  value={form.spreadsheet_id}
                  onChange={(e) => setForm({ ...form, spreadsheet_id: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="worksheet_name">Имя листа</Label>
                <Input
                  id="worksheet_name"
                  placeholder="Sheet1"
                  value={form.worksheet_name}
                  onChange={(e) => setForm({ ...form, worksheet_name: e.target.value })}
                  required
                />
              </div>

              {createMutation.error && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                  {(createMutation.error as Error).message}
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Создаём…' : 'Создать'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {isLoading ? (
        <TableSkeleton columns={['Название', 'Spreadsheet ID', 'Worksheet', 'Доступ', '']} rows={3} />
      ) : (
      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Название</TableHead>
              <TableHead>Spreadsheet ID</TableHead>
              <TableHead>Worksheet</TableHead>
              <TableHead>Доступ</TableHead>
              <TableHead className="w-12 text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {!isLoading && data.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Пока нет таблиц.
                </TableCell>
              </TableRow>
            )}
            {data.map((sheet) => {
              const check = checkResults[sheet.id];
              return (
                <TableRow key={sheet.id}>
                  <TableCell className="font-medium">{sheet.display_name}</TableCell>
                  <TableCell>
                    <code className="rounded bg-muted px-2 py-0.5 text-xs">{sheet.spreadsheet_id}</code>
                  </TableCell>
                  <TableCell>
                    <code className="rounded bg-muted px-2 py-0.5 text-xs">{sheet.worksheet_name}</code>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => void checkAccess(sheet)}>
                        Проверить
                      </Button>
                      {check?.ok && (
                        <span className="flex items-center gap-1 text-xs text-success">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          OK
                        </span>
                      )}
                      {check && !check.ok && (
                        <span className="flex items-center gap-1 text-xs text-destructive" title={check.error}>
                          <ShieldAlert className="h-3.5 w-3.5" />
                          Нет доступа
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => {
                        if (window.confirm(`Удалить «${sheet.display_name}»?`)) {
                          deleteMutation.mutate(sheet.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      )}
    </div>
  );
}
